import { useMemo } from 'react';
import { fetchApiRef, useApi } from '@backstage/frontend-plugin-api';
import type {
  Application,
  AuditEntry,
  CatalogUser,
  DeletedApplication,
  DocNode,
  Editing,
  KeyUser,
  Me,
  PackageInfo,
  RestoreResult,
  UploadResult,
} from '../types';

const BASE = 'plugin://ad-ams';

/** Tries after the first one; waits 1, 2, 4 and 8 s between them. */
const RETRIES = 4;

export function useAdAmsApi() {
  const { fetch } = useApi(fetchApiRef);

  return useMemo(() => {
    const send = async (path: string, init?: RequestInit): Promise<Response> => {
      const response = await fetch(`${BASE}${path}`, init);
      if (!response.ok) {
        // Plugin errors come as `{ error: string }`; Backstage's default
        // handler (e.g. 401) as `{ error: { message } }`.
        const body = await response.json().catch(() => ({}));
        const message = typeof body.error === 'string' ? body.error : body.error?.message;
        throw Object.assign(new Error(message ?? `HTTP ${response.status}`), {
          status: response.status,
        });
      }
      return response;
    };
    const getJson = async <T>(path: string, init?: RequestInit): Promise<T> =>
      (await send(path, init)).json();
    const jsonInit = (method: string, body: unknown): RequestInit => ({
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    /**
     * Tries again what may work on a second go: a dropped connection (fetch
     * rejects with a TypeError), a storage or proxy failure (5xx), and `also`.
     */
    const withRetry = async <T>(attempt: () => Promise<T>, also: number[] = []): Promise<T> => {
      for (let tries = 0; ; tries++) {
        try {
          return await attempt();
        } catch (error) {
          const status = (error as { status?: number }).status ?? 0;
          const retryable =
            error instanceof TypeError || status >= 500 || [408, 429, ...also].includes(status);
          if (!retryable || tries >= RETRIES) {
            throw error;
          }
          await new Promise(resolve => setTimeout(resolve, 1000 * 2 ** tries));
        }
      }
    };

    return {
      me: () => getJson<Me>('/me'),
      listApplications: () => getJson<Application[]>('/applications'),
      createApplication: (code: string, name: string) =>
        getJson<Application>('/applications', jsonInit('POST', { code, name })),
      deleteApplication: async (appId: string) => {
        await send(`/applications/${appId}`, { method: 'DELETE' });
      },
      listDeletedApplications: () => getJson<DeletedApplication[]>('/deleted-applications'),
      restoreApplication: async (appId: string) => {
        await send(`/deleted-applications/${appId}/restore`, { method: 'POST' });
      },
      getTree: (appId: string) =>
        getJson<{
          application: Application;
          nodes: DocNode[];
          highestPackageLabel: string | null;
          currentPackage: PackageInfo | null;
        }>(`/applications/${appId}/tree`),
      // The name goes to the banner and the audit.
      startEditing: (appId: string, displayName?: string) =>
        getJson<Editing>(`/applications/${appId}/editing`, jsonInit('POST', { displayName })),
      endEditing: async (appId: string) => {
        await send(`/applications/${appId}/editing`, { method: 'DELETE' });
      },
      /**
       * Sends a file in parts of the size the backend asks for, one request
       * each, trying every part again when the network or the storage fails.
       * The backend checks the file before any part goes.
       */
      uploadFile: async (
        appId: string,
        relativePath: string,
        file: File,
        publishId: string,
        packageLabel?: string,
        onProgress?: (sentBytes: number) => void,
      ): Promise<UploadResult> => {
        const uploads = `/applications/${appId}/uploads`;
        const { uploadId, partSizeBytes } = await withRetry(() =>
          getJson<{ uploadId: string; partSizeBytes: number }>(
            uploads,
            jsonInit('POST', { relativePath, sizeBytes: file.size, publishId, packageLabel }),
          ),
        );
        try {
          for (let part = 1, offset = 0; offset < file.size; part++, offset += partSizeBytes) {
            const body = file.slice(offset, offset + partSizeBytes);
            await withRetry(() =>
              send(`${uploads}/${uploadId}/parts/${part}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/octet-stream' },
                body,
              }),
            );
            onProgress?.(Math.min(offset + partSizeBytes, file.size));
          }
          // 409 while a completion whose answer got lost is still running. A
          // completion the backend refused ends the upload there, so a try
          // after it finds nothing (404): the refusal is what to show.
          let refusal: Error | undefined;
          return await withRetry(async () => {
            try {
              return await getJson<UploadResult>(`${uploads}/${uploadId}/complete`, {
                method: 'POST',
              });
            } catch (error) {
              const status = (error as { status?: number }).status;
              if (refusal && status === 404) {
                throw Object.assign(new Error(refusal.message), { status });
              }
              if (status) {
                refusal = error as Error;
              }
              throw error;
            }
          }, [409]);
        } catch (error) {
          // Drops the parts sent and records the file as rejected in the audit.
          await send(
            `${uploads}/${uploadId}`,
            jsonInit('DELETE', { reason: (error as Error).message }),
          ).catch(() => {});
          throw error;
        }
      },
      fetchContent: async (nodeId: string) => (await send(`/nodes/${nodeId}/content`)).blob(),
      listKeyUsers: (appId: string) => getJson<KeyUser[]>(`/applications/${appId}/key-users`),
      // The backend links the ref the user signs in as, which it derives from
      // the email; the name goes to the audit.
      addKeyUser: async (appId: string, user: CatalogUser) => {
        await send(
          `/applications/${appId}/key-users`,
          jsonInit('POST', {
            userEntityRef: user.ref,
            email: user.email,
            displayName: user.displayName,
          }),
        );
      },
      removeKeyUser: async (appId: string, userEntityRef: string) => {
        await send(
          `/applications/${appId}/key-users?userEntityRef=${encodeURIComponent(userEntityRef)}`,
          { method: 'DELETE' },
        );
      },
      /** Frees the application for the next publish; each release of a publish ends here. */
      endPublish: async (appId: string, publishId: string) => {
        await send(`/applications/${appId}/publishing/${publishId}`, { method: 'DELETE' });
      },
      prunePackage: (
        appId: string,
        keepPaths: string[],
        publishId: string,
        packageLabel?: string,
      ) =>
        getJson<{ removed: number }>(
          `/applications/${appId}/packages/prune`,
          jsonInit('POST', { keepPaths, publishId, packageLabel }),
        ),
      listPackages: (appId: string) =>
        getJson<PackageInfo[]>(`/applications/${appId}/packages`),
      getPackageTree: (appId: string, publishId: string) =>
        getJson<DocNode[]>(`/applications/${appId}/packages/${publishId}/tree`),
      restorePackage: (appId: string, publishId: string) =>
        getJson<RestoreResult>(`/applications/${appId}/packages/${publishId}/restore`, {
          method: 'POST',
        }),
      listPublishItems: (appId: string, publishId: string) =>
        getJson<AuditEntry[]>(`/applications/${appId}/audit/publishes/${publishId}`),
      fetchVersionContent: async (appId: string, versionId: string) =>
        (await send(`/applications/${appId}/versions/${versionId}/content`)).blob(),
      listAudit: (
        appId: string,
        limit: number,
        offset: number,
        filters: { q?: string; action?: string } = {},
      ) => {
        const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
        if (filters.q) {
          params.set('q', filters.q);
        }
        if (filters.action) {
          params.set('action', filters.action);
        }
        return getJson<{ items: AuditEntry[]; total: number }>(
          `/applications/${appId}/audit?${params}`,
        );
      },
      listCatalogUsers: async (): Promise<CatalogUser[]> => {
        const fields =
          'limit=500&fields=metadata.name,metadata.namespace,spec.profile.displayName,spec.profile.email';
        const users: CatalogUser[] = [];
        // The catalog pages its results; follow the cursor so every user is selectable.
        let query = `filter=kind=user&${fields}`;
        for (;;) {
          const response = await fetch(`plugin://catalog/entities/by-query?${query}`);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const body = (await response.json()) as {
            items: Array<{
              metadata: { name: string; namespace?: string };
              spec?: { profile?: { displayName?: string; email?: string } };
            }>;
            pageInfo?: { nextCursor?: string };
          };
          for (const item of body.items) {
            const profile = item.spec?.profile;
            users.push({
              ref: `user:${item.metadata.namespace ?? 'default'}/${item.metadata.name}`
                .toLocaleLowerCase('en-US'),
              label: profile?.displayName
                ? `${profile.displayName} (${profile.email ?? item.metadata.name})`
                : item.metadata.name,
              ...(profile?.displayName ? { displayName: profile.displayName } : {}),
              ...(profile?.email ? { email: profile.email } : {}),
            });
          }
          const cursor = body.pageInfo?.nextCursor;
          if (!cursor) {
            return users;
          }
          query = `cursor=${encodeURIComponent(cursor)}&${fields}`;
        }
      },
    };
  }, [fetch]);
}
